import { z } from 'zod';

export const FILE_QUESTION_TYPE_KEY = 'file';

export const ATTENDEE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const QUESTION_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export const ACCEPTED_FILE_TYPES_RULE = 'acceptedFileTypes';

export type FileTypeGroup = 'pdf' | 'image' | 'word';

export const ALL_FILE_TYPE_GROUPS: FileTypeGroup[] = ['pdf', 'image', 'word'];

export const FILE_TYPE_GROUP_LABELS: Record<FileTypeGroup, string> = {
	pdf: 'PDF',
	image: 'Images (JPG, PNG, HEIC)',
	word: 'Word documents'
};

// The extension decides the content type, not the browser's file.type: Chrome
// and Firefox report HEIC as an empty string, and the content type is signed
// into the upload policy so it has to be known up front.
const EXTENSION_MIME_TYPES: Record<FileTypeGroup, Record<string, string>> = {
	pdf: { pdf: 'application/pdf' },
	image: {
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		heic: 'image/heic',
		heif: 'image/heif'
	},
	word: {
		doc: 'application/msword',
		docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
	}
};

function isFileTypeGroup(value: string): value is FileTypeGroup {
	return (ALL_FILE_TYPE_GROUPS as string[]).includes(value);
}

export function parseAcceptedFileTypes(rule: string | null | undefined): FileTypeGroup[] {
	const groups = (rule ?? '')
		.split(',')
		.map((part) => part.trim())
		.filter(isFileTypeGroup);
	return groups.length ? groups : [...ALL_FILE_TYPE_GROUPS];
}

export function acceptedFileTypesFromRules(
	rules: { validationType: string; validationValue: string | null }[] | null | undefined
): FileTypeGroup[] {
	return parseAcceptedFileTypes(rules?.find((rule) => rule.validationType === ACCEPTED_FILE_TYPES_RULE)?.validationValue);
}

export function serializeAcceptedFileTypes(groups: FileTypeGroup[]): string {
	return groups.join(',');
}

export function resolveUploadType(
	fileName: string,
	groups: FileTypeGroup[]
): { extension: string; mimeType: string } | null {
	const dot = fileName.lastIndexOf('.');
	if (dot <= 0) return null;
	const extension = fileName.slice(dot + 1).toLowerCase();
	for (const group of groups) {
		const mimeType = EXTENSION_MIME_TYPES[group][extension];
		if (mimeType) return { extension, mimeType };
	}
	return null;
}

export function acceptAttribute(groups: FileTypeGroup[]): string {
	return groups.flatMap((group) => Object.keys(EXTENSION_MIME_TYPES[group]).map((ext) => `.${ext}`)).join(',');
}

export type FileAnswer = { uploadId: string; fileName: string };

const fileAnswerObject = z.object({
	uploadId: z.guid(),
	fileName: z.string().min(1)
});

function parseJsonString(value: unknown): unknown {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

export function readFileAnswer(value: unknown): FileAnswer | null {
	const parsed = fileAnswerObject.safeParse(parseJsonString(value));
	return parsed.success ? parsed.data : null;
}

export function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${Number(kb.toFixed(1))} KB`;
	return `${Number((kb / 1024).toFixed(1))} MB`;
}
